import React, { useState, useRef } from 'react';
import { motion, useScroll, useTransform, AnimatePresence, useSpring, useMotionValue, useMotionTemplate } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  LuCloud, LuCheck, LuShield, LuZap, LuActivity, 
  LuServer, LuLock, LuTrendingUp, LuX, LuCircleAlert, LuGlobe,
  LuLayoutDashboard, LuDatabase, LuCpu, LuHardDrive, LuNetwork, LuTerminal
} from 'react-icons/lu';
import { FaAws, FaDocker } from 'react-icons/fa';
import { VscAzure } from 'react-icons/vsc';
import { SiOracle, SiLinux, SiKubernetes } from 'react-icons/si';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

// --- Components ---

const SpotlightCard = ({ children, className = "" }) => {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  function handleMouseMove({ currentTarget, clientX, clientY }) {
    const { left, top } = currentTarget.getBoundingClientRect();
    mouseX.set(clientX - left);
    mouseY.set(clientY - top);
  }

  return (
    <div
      className={`group relative border border-zinc-800 bg-zinc-900/50 overflow-hidden ${className}`}
      onMouseMove={handleMouseMove}
    >
      <motion.div
        className="pointer-events-none absolute -inset-px rounded-xl opacity-0 transition duration-300 group-hover:opacity-100"
        style={{
          background: useMotionTemplate`
            radial-gradient(
              650px circle at ${mouseX}px ${mouseY}px,
              rgba(245, 158, 11, 0.15),
              transparent 80%
            )
          `,
        }}
      />
      <div className="relative h-full">{children}</div>
    </div>
  );
};

const MagneticButton = ({ children, className = "", ...props }) => {
  const ref = useRef(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const handleMouse = (e) => {
    const { clientX, clientY } = e;
    const { height, width, left, top } = ref.current.getBoundingClientRect();
    const middleX = clientX - (left + width / 2);
    const middleY = clientY - (top + height / 2);
    setPosition({ x: middleX * 0.1, y: middleY * 0.1 });
  };

  const reset = () => {
    setPosition({ x: 0, y: 0 });
  };

  const { x, y } = position;
  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouse}
      onMouseLeave={reset}
      animate={{ x, y }}
      transition={{ type: "spring", stiffness: 150, damping: 15, mass: 0.1 }}
    >
      <Button className={className} {...props}>
        {children}
      </Button>
    </motion.div>
  );
};

const LandingPage = () => {
  const registrationEnabled = process.env.REACT_APP_ALLOW_REGISTRATION === 'true';
  const { scrollYProgress } = useScroll();
  const scaleX = useTransform(scrollYProgress, [0, 1], [0, 1]);
  const [selectedImage, setSelectedImage] = useState(null);

  // "Fly In/Out" Animation Variants
  const flyInOut = {
    hidden: { 
      opacity: 0, 
      y: 100, 
      scale: 0.9, 
      filter: "blur(10px)",
      rotateX: 10
    },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1, 
      filter: "blur(0px)",
      rotateX: 0,
      transition: { 
        duration: 0.8, 
        ease: [0.22, 1, 0.36, 1] // Custom ease
      }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50 font-sans selection:bg-amber-500/30 overflow-x-hidden perspective-1000">
      {/* Scroll Progress Bar */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-500 to-orange-600 origin-left z-[60]"
        style={{ scaleX }}
      />

      {/* Image Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0, backdropFilter: "blur(0px)" }}
            animate={{ opacity: 1, backdropFilter: "blur(10px)" }}
            exit={{ opacity: 0, backdropFilter: "blur(0px)" }}
            onClick={() => setSelectedImage(null)}
            className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4 cursor-zoom-out"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 50 }}
              transition={{ type: "spring", bounce: 0.3 }}
              className="relative max-w-7xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={selectedImage}
                alt="Enlarged view"
                className="w-full h-auto rounded-xl shadow-2xl border border-zinc-800"
              />
              <button 
                onClick={() => setSelectedImage(null)}
                className="absolute -top-12 right-0 text-zinc-400 hover:text-white transition-colors"
              >
                <LuX className="w-8 h-8" />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-zinc-950/80 backdrop-blur-md border-b border-zinc-800/50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/20">
              <LuCloud className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">
              Cloud Watcher
            </span>
          </div>
          <div className="hidden md:flex items-center gap-6">
            <Link to="/auth">
              <Button variant="ghost" className="text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800/50">
                Log In
              </Button>
            </Link>
            <Link to="/auth">
              <MagneticButton className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-medium shadow-lg shadow-amber-500/20">
                {registrationEnabled ? 'Start Free Trial' : 'Sign In'}
              </MagneticButton>
            </Link>
          </div>
        </div>
      </nav>

      {/* 1. Hero Section */}
      <section className="relative pt-32 pb-20 px-4 md:px-6 lg:px-8 overflow-hidden min-h-[90vh] flex items-center">
        <div className="max-w-7xl mx-auto w-full grid lg:grid-cols-2 gap-12 items-center">
          <motion.div 
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="space-y-8 text-center lg:text-left z-10"
          >
            <motion.div variants={flyInOut}>
              <Badge variant="outline" className="mb-6 border-amber-500/30 text-amber-500 bg-amber-500/5 px-4 py-1.5 text-sm backdrop-blur-sm">
                v2.0 Now Available
              </Badge>
              <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-[1.1] mb-6">
                Tame the <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-orange-400 to-amber-400 animate-gradient-x">
                  Multi-Cloud Chaos
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-zinc-400 max-w-2xl mx-auto lg:mx-0 leading-relaxed">
                The operator-first platform for managing AWS, Azure, and private infrastructure. No more tab switching.
              </p>
            </motion.div>

            <motion.div 
              variants={flyInOut}
              className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 pt-4"
            >
              <Link to="/auth">
                <MagneticButton size="lg" className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-bold h-14 px-8 text-lg w-full sm:w-auto shadow-xl shadow-amber-500/20">
                  {registrationEnabled ? 'Get Started Free' : 'Open Cloud Watcher'}
                </MagneticButton>
              </Link>
              <MagneticButton variant="outline" size="lg" className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white h-14 px-8 text-lg w-full sm:w-auto backdrop-blur-sm bg-zinc-900/30">
                View Documentation
              </MagneticButton>
            </motion.div>

            <motion.div variants={flyInOut} className="pt-8 flex items-center justify-center lg:justify-start gap-8 text-zinc-500 grayscale opacity-70">
              <FaAws className="w-8 h-8 hover:grayscale-0 transition-all duration-300 hover:text-[#FF9900]" />
              <VscAzure className="w-8 h-8 hover:grayscale-0 transition-all duration-300 hover:text-[#0089D6]" />
              <SiOracle className="w-8 h-8 hover:grayscale-0 transition-all duration-300 hover:text-[#F80000]" />
              <SiLinux className="w-8 h-8 hover:grayscale-0 transition-all duration-300 hover:text-zinc-200" />
              <SiKubernetes className="w-8 h-8 hover:grayscale-0 transition-all duration-300 hover:text-[#326CE5]" />
            </motion.div>
          </motion.div>

          {/* Hero Visual */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.8, rotateY: -20 }}
            animate={{ opacity: 1, scale: 1, rotateY: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="relative hidden lg:block perspective-1000"
          >
            <div className="absolute inset-0 bg-gradient-to-tr from-amber-500/20 to-purple-500/20 rounded-full blur-[100px] -z-10" />
            <SpotlightCard className="rounded-xl border-zinc-800 bg-zinc-950/80 backdrop-blur-xl shadow-2xl transform rotate-y-12 hover:rotate-y-0 transition-transform duration-700 ease-out">
              <div className="p-4 border-b border-zinc-800 flex items-center gap-2">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-500/20 border border-red-500/50"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-500/20 border border-yellow-500/50"></div>
                  <div className="w-3 h-3 rounded-full bg-green-500/20 border border-green-500/50"></div>
                </div>
                <div className="ml-4 text-xs text-zinc-500 font-mono flex-1 text-center">cloud-control-plane</div>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex gap-4">
                  <div className="flex-1 bg-zinc-900/50 rounded-lg p-4 border border-zinc-800">
                    <div className="text-xs text-zinc-500 mb-1">Total Resources</div>
                    <div className="text-2xl font-mono text-white">1,248</div>
                  </div>
                  <div className="flex-1 bg-zinc-900/50 rounded-lg p-4 border border-zinc-800">
                    <div className="text-xs text-zinc-500 mb-1">Monthly Spend</div>
                    <div className="text-2xl font-mono text-white">$4,290</div>
                  </div>
                </div>
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-12 bg-zinc-900/30 rounded border border-zinc-800/50 flex items-center px-3 gap-3">
                      <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                      <div className="h-2 w-24 bg-zinc-800 rounded"></div>
                      <div className="h-2 w-12 bg-zinc-800 rounded ml-auto"></div>
                    </div>
                  ))}
                </div>
              </div>
            </SpotlightCard>
          </motion.div>
        </div>
      </section>

      {/* 2. Unified Infrastructure (Animated Providers) */}
      <motion.section 
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false, margin: "-20% 0px -20% 0px" }}
        variants={flyInOut}
        className="py-24 px-4 md:px-6 lg:px-8 relative overflow-hidden bg-zinc-900/20 border-y border-zinc-800/50"
      >
        <div className="max-w-6xl mx-auto relative h-80 flex items-center justify-center">
          {/* Central Hub */}
          <motion.div 
            initial={{ scale: 0.8, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative z-10 w-24 h-24 rounded-2xl bg-zinc-900 border border-zinc-700 flex items-center justify-center shadow-2xl shadow-amber-500/10"
          >
            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
              <LuCloud className="w-8 h-8 text-white" />
            </div>
          </motion.div>

          {/* Orbiting Providers */}
          {[
            { Icon: FaAws, color: "text-[#FF9900]", label: "AWS", delay: 0, x: -140, y: -70 },
            { Icon: VscAzure, color: "text-[#0089D6]", label: "Azure", delay: 0.2, x: 140, y: -70 },
            { Icon: SiOracle, color: "text-[#F80000]", label: "Oracle", delay: 0.4, x: -140, y: 70 },
            { Icon: SiLinux, color: "text-zinc-300", label: "Linux", delay: 0.6, x: 140, y: 70 },
          ].map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: 0, y: 0 }}
              whileInView={{ opacity: 1, x: item.x, y: item.y }}
              transition={{ duration: 1.2, delay: item.delay, type: "spring", bounce: 0.4 }}
              className="absolute z-0"
            >
              <div className="w-20 h-20 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center shadow-lg hover:shadow-xl hover:shadow-amber-500/20 transition-all duration-300">
                <item.Icon className={`w-10 h-10 ${item.color}`} />
              </div>
              <div className="absolute top-full mt-3 left-1/2 -translate-x-1/2 text-xs font-semibold text-zinc-300 whitespace-nowrap">
                {item.label}
              </div>
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-24 space-y-4">
          <p className="text-zinc-500 text-sm font-medium uppercase tracking-widest">
            Unified Infrastructure Control Plane
          </p>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            Manage AWS, Azure, Oracle, and self-hosted infrastructure from a single pane of glass
          </p>
        </div>
      </motion.section>

      {/* 3. Dashboard Section (Expanded) */}
      <motion.section 
        initial="hidden"
        whileInView="visible"
        exit="hidden"
        viewport={{ once: false, margin: "-20% 0px -20% 0px" }}
        variants={flyInOut}
        className="py-32 px-4 md:px-6 lg:px-8 relative"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-5 space-y-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-500 text-sm font-medium">
                <LuLayoutDashboard className="w-4 h-4" />
                <span>Unified Dashboard</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight">
                The Single Pane of Glass <br />
                <span className="text-zinc-500">That Actually Works.</span>
              </h2>
              <p className="text-lg text-zinc-400 leading-relaxed">
                Stop logging into five different consoles just to check if your systems are online. Cloud Watcher aggregates health, cost, and performance metrics from every provider into one customizable view.
              </p>
              
              <div className="grid grid-cols-2 gap-4 pt-4">
                {[
                  { icon: LuActivity, label: "Real-time Health" },
                  { icon: LuTrendingUp, label: "Cost Velocity" },
                  { icon: LuCircleAlert, label: "Unified Alerts" },
                  { icon: LuGlobe, label: "Global Latency" }
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-zinc-900/50 border border-zinc-800">
                    <item.icon className="w-5 h-5 text-amber-500" />
                    <span className="text-zinc-300 font-medium">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-7 relative">
              <SpotlightCard className="rounded-2xl border-zinc-800 bg-zinc-950 shadow-2xl group cursor-zoom-in">
                <div onClick={() => setSelectedImage('/screenshots/dashboard-full.png')}>
                  <img 
                    src="/screenshots/dashboard-full.png" 
                    alt="Dashboard" 
                    className="w-full rounded-xl opacity-90 transition-opacity group-hover:opacity-100"
                  />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/20 backdrop-blur-[2px]">
                    <span className="bg-zinc-900 text-white px-4 py-2 rounded-full text-sm font-medium border border-zinc-700 shadow-xl">
                      View Full Dashboard
                    </span>
                  </div>
                </div>
              </SpotlightCard>
              
              {/* Floating Widget: Health Status */}
              <motion.div 
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="absolute -bottom-8 -left-8 bg-zinc-900 p-4 rounded-xl border border-zinc-800 shadow-2xl w-64 hidden md:block"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-bold text-zinc-400 uppercase tracking-wider">System Health</span>
                  <span className="text-xs text-green-500 font-mono">99.99%</span>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-zinc-300">AWS (us-east-1)</span>
                    <LuCheck className="w-4 h-4 text-green-500" />
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-zinc-300">Azure (West EU)</span>
                    <LuCheck className="w-4 h-4 text-green-500" />
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-zinc-300">Oracle (Phoenix)</span>
                    <LuCheck className="w-4 h-4 text-green-500" />
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* 4. Resources Section (Expanded) */}
      <motion.section 
        initial="hidden"
        whileInView="visible"
        exit="hidden"
        viewport={{ once: false, margin: "-20% 0px -20% 0px" }}
        variants={flyInOut}
        className="py-32 px-4 md:px-6 lg:px-8 bg-zinc-900/20 border-y border-zinc-800/50"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-16 items-center lg:flex-row-reverse">
            <div className="lg:col-span-7 lg:order-2 relative">
               <SpotlightCard className="rounded-2xl border-zinc-800 bg-zinc-950 shadow-2xl overflow-hidden min-h-[400px]">
                 <div className="p-4 border-b border-zinc-800 bg-zinc-900/50 flex items-center justify-between">
                   <div className="flex items-center gap-2 bg-zinc-950 border border-zinc-800 rounded-md px-3 py-1.5 w-full max-w-md">
                     <LuCloud className="text-zinc-500 w-4 h-4" />
                     <span className="text-zinc-400 text-sm">tag:production AND type:database</span>
                   </div>
                   <div className="flex gap-2">
                     <div className="w-3 h-3 rounded-full bg-zinc-800"></div>
                     <div className="w-3 h-3 rounded-full bg-zinc-800"></div>
                   </div>
                 </div>
                 <div className="p-0">
                   {[
                     { name: "prod-db-primary", type: "RDS Postgres", loc: "us-east-1", prov: "AWS", icon: FaAws, color: "text-[#FF9900]" },
                     { name: "analytics-warehouse", type: "Synapse", loc: "w-europe", prov: "Azure", icon: VscAzure, color: "text-[#0089D6]" },
                     { name: "legacy-oracle-01", type: "Bare Metal", loc: "phx-1", prov: "Oracle", icon: SiOracle, color: "text-[#F80000]" },
                     { name: "cache-cluster-redis", type: "ElastiCache", loc: "us-east-1", prov: "AWS", icon: FaAws, color: "text-[#FF9900]" },
                     { name: "k8s-worker-pool", type: "AKS Cluster", loc: "w-europe", prov: "Azure", icon: SiKubernetes, color: "text-[#326CE5]" },
                   ].map((res, i) => (
                     <div key={i} className="flex items-center justify-between p-4 border-b border-zinc-800/50 hover:bg-zinc-900/50 transition-colors group">
                       <div className="flex items-center gap-4">
                         <div className="p-2 rounded-lg bg-zinc-900 border border-zinc-800 group-hover:border-zinc-700">
                           <res.icon className={`w-5 h-5 ${res.color}`} />
                         </div>
                         <div>
                           <div className="text-sm font-medium text-zinc-200">{res.name}</div>
                           <div className="text-xs text-zinc-500 flex items-center gap-2">
                             {res.prov} • {res.loc}
                           </div>
                         </div>
                       </div>
                       <div className="flex items-center gap-4">
                         <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/20 text-xs">Running</Badge>
                         <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-zinc-500 hover:text-white">
                           <LuTerminal className="w-4 h-4" />
                         </Button>
                       </div>
                     </div>
                   ))}
                 </div>
               </SpotlightCard>
            </div>

            <div className="lg:col-span-5 lg:order-1 space-y-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-500 text-sm font-medium">
                <LuServer className="w-4 h-4" />
                <span>Global Inventory</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight">
                Manage Resources, <br />
                <span className="text-zinc-500">Not Just Accounts.</span>
              </h2>
              <p className="text-lg text-zinc-400 leading-relaxed">
                Forget about regions and subscriptions. Search for "production database" and find it instantly, whether it's on AWS, Azure, or a server under your desk.
              </p>
              
              <div className="space-y-4">
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-xl bg-zinc-900 border border-zinc-800 flex items-center justify-center shrink-0">
                    <LuTerminal className="w-6 h-6 text-blue-500" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white">Unified Actions</h4>
                    <p className="text-zinc-400 text-sm">Restart, stop, or resize resources across providers with a single click.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-xl bg-zinc-900 border border-zinc-800 flex items-center justify-center shrink-0">
                    <LuDatabase className="w-6 h-6 text-blue-500" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white">Universal Tagging</h4>
                    <p className="text-zinc-400 text-sm">Apply tags that persist across clouds for unified cost allocation.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* 5. Analytics Section (Expanded) */}
      <motion.section 
        initial="hidden"
        whileInView="visible"
        exit="hidden"
        viewport={{ once: false, margin: "-20% 0px -20% 0px" }}
        variants={flyInOut}
        className="py-32 px-4 md:px-6 lg:px-8 relative"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-5 space-y-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/10 border border-green-500/20 text-green-500 text-sm font-medium">
                <LuTrendingUp className="w-4 h-4" />
                <span>Cost Intelligence</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight">
                Financial Clarity <br />
                <span className="text-zinc-500">For Engineering Teams.</span>
              </h2>
              <p className="text-lg text-zinc-400 leading-relaxed">
                Don't wait for the CFO to yell at you. See real-time cost accumulation, forecast next month's bill, and identify waste immediately.
              </p>
              
              <div className="bg-zinc-900/50 rounded-xl border border-zinc-800 p-6 space-y-6">
                <div className="flex items-center justify-between">
                  <span className="text-zinc-400">Current Month Forecast</span>
                  <span className="text-2xl font-mono text-white">$12,450</span>
                </div>
                <div className="w-full bg-zinc-800 h-2 rounded-full overflow-hidden">
                  <div className="bg-green-500 h-full w-[75%]"></div>
                </div>
                <div className="flex justify-between text-xs text-zinc-500">
                  <span>$0</span>
                  <span>Budget: $15,000</span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7 relative">
              <SpotlightCard className="rounded-2xl border-zinc-800 bg-zinc-950 shadow-2xl group cursor-zoom-in">
                <div onClick={() => setSelectedImage('/screenshots/analytics.png')}>
                  <img 
                    src="/screenshots/analytics.png" 
                    alt="Analytics" 
                    className="w-full rounded-xl opacity-90 transition-opacity group-hover:opacity-100"
                  />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/20 backdrop-blur-[2px]">
                    <span className="bg-zinc-900 text-white px-4 py-2 rounded-full text-sm font-medium border border-zinc-700 shadow-xl">
                      View Cost Reports
                    </span>
                  </div>
                </div>
              </SpotlightCard>

              {/* Floating Widget: Anomaly Detection */}
              <motion.div 
                initial={{ x: 20, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="absolute -top-6 -right-6 bg-zinc-900 p-4 rounded-xl border border-red-500/30 shadow-2xl w-72 hidden md:block"
              >
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-red-500/10 rounded-lg shrink-0">
                    <LuCircleAlert className="w-5 h-5 text-red-500" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-zinc-200">Cost Anomaly Detected</h4>
                    <p className="text-xs text-zinc-400 mt-1 leading-relaxed">
                      <span className="text-zinc-300 font-medium">Azure-East-US</span> database compute increased by <span className="text-red-400">+240%</span> in the last 4 hours.
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* 6. CTA Section */}
      <motion.section 
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false }}
        variants={flyInOut}
        className="py-32 px-4 text-center relative overflow-hidden"
      >
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-amber-900/20 via-zinc-950 to-zinc-950"></div>
        <div className="max-w-3xl mx-auto space-y-8">
          <h2 className="text-4xl md:text-6xl font-bold text-white tracking-tight">
            Ready to regain control?
          </h2>
          <p className="text-xl text-zinc-400">
            Join engineering teams who sleep better at night.
          </p>
          <div className="pt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/auth">
              <MagneticButton size="lg" className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-bold h-16 px-12 text-xl shadow-2xl shadow-amber-500/20 w-full sm:w-auto">
                {registrationEnabled ? 'Start Free Trial' : 'Sign In'}
              </MagneticButton>
            </Link>
            <MagneticButton variant="outline" size="lg" className="border-zinc-800 text-zinc-400 hover:text-white h-16 px-12 text-xl w-full sm:w-auto bg-zinc-900/50">
              Schedule Demo
            </MagneticButton>
          </div>
        </div>
      </motion.section>

      {/* Footer */}
      <footer className="py-12 border-t border-zinc-900 bg-zinc-950 text-zinc-500 text-sm text-center">
        <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <LuCloud className="w-4 h-4" />
            <span className="font-semibold text-zinc-300">Cloud Watcher</span>
          </div>
          <div className="flex gap-8">
            <a href="#" className="hover:text-zinc-300 transition-colors">Privacy</a>
            <a href="#" className="hover:text-zinc-300 transition-colors">Terms</a>
            <a href="#" className="hover:text-zinc-300 transition-colors">Twitter</a>
            <a href="#" className="hover:text-zinc-300 transition-colors">GitHub</a>
          </div>
          <p>© 2025 Cloud Watcher Inc.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
